import {
  ANALYTICS_CONSENT,
  acceptAnalyticsConsent as persistAcceptedConsent,
  readAnalyticsConsent,
  rejectAnalyticsConsent as persistRejectedConsent,
  resetAnalyticsConsent as persistResetConsent,
} from "./analyticsConsent";

const MAX_QUEUED_EVENTS = 50;
const DEFAULT_POSTHOG_HOST = "https://eu.i.posthog.com";

const CTA_EVENT_ALIASES = {
  cta_strategy_call_click: "strategy_call",
  cta_lead_magnet_click: "lead_magnet",
};

let posthogClient = null;
let posthogInitPromise = null;
let posthogLoader = () => import("posthog-js").then((module) => module.default || module);
let eventQueue = [];
let leadIdHasher = hashLeadEmail;

function hasBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function getEnv(name) {
  return process.env?.[name] || "";
}

function getProjectToken() {
  return getEnv("REACT_APP_POSTHOG_PROJECT_TOKEN") || getEnv("REACT_APP_POSTHOG_KEY");
}

export function getCurrentPath() {
  if (!hasBrowser()) return "";
  return window.location?.pathname || "/";
}

export function getPageGroup(path = getCurrentPath()) {
  if (path === "/") return "home";
  if (path === "/about") return "about";
  if (path === "/services") return "services";
  if (path.startsWith("/services/")) return "service_detail";
  if (path === "/contact") return "contact";
  if (path === "/work" || path === "/cases") return "work";

  return "other";
}

function getLocale() {
  if (!hasBrowser()) return "";
  return document.documentElement.lang || "";
}

function isLocalhost() {
  if (!hasBrowser()) return false;

  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

export function isExternalAnalyticsEnabled() {
  if (!hasBrowser()) return false;
  if (getEnv("REACT_APP_ENABLE_ANALYTICS") !== "true") return false;
  if (!getProjectToken()) return false;
  if (isLocalhost() && getEnv("REACT_APP_ENABLE_ANALYTICS_DEV") !== "true") return false;

  return true;
}

function normalizeEvent(name, params = {}) {
  const legacyCtaType = CTA_EVENT_ALIASES[name];
  const eventName = legacyCtaType ? "cta_click" : name;
  const path = params.path || getCurrentPath();
  const normalizedParams = {
    ...params,
    path,
  };

  if (!normalizedParams.locale) {
    normalizedParams.locale = getLocale();
  }

  if (
    eventName === "cta_click" ||
    eventName === "navigation_click" ||
    eventName === "section_view" ||
    eventName === "lead_magnet_view" ||
    eventName === "page_view"
  ) {
    normalizedParams.page_group = normalizedParams.page_group || getPageGroup(path);
  }

  if (legacyCtaType) {
    normalizedParams.legacy_event_name = name;
    normalizedParams.source = normalizedParams.source || legacyCtaType;
    normalizedParams.label =
      normalizedParams.label || normalizedParams.cta || normalizedParams.source;
  }

  if (eventName === "lead_magnet_submit" || eventName === "lead_magnet_form_start") {
    normalizedParams.asset_key =
      normalizedParams.asset_key || normalizedParams.assetKey || normalizedParams.niche || "";
  }

  return {
    name: eventName,
    params: normalizedParams,
  };
}

function dispatchInternalEvent(event) {
  if (!hasBrowser()) return;

  window.dispatchEvent(
    new CustomEvent("makdis:analytics", {
      detail: event,
    })
  );
}

function pushQueue(event) {
  eventQueue.push(event);

  if (eventQueue.length > MAX_QUEUED_EVENTS) {
    eventQueue = eventQueue.slice(eventQueue.length - MAX_QUEUED_EVENTS);
  }
}

function captureWithPostHog(event) {
  if (!posthogClient || typeof posthogClient.capture !== "function") return;
  if (event.options) {
    posthogClient.capture(event.name, event.params, event.options);
    return;
  }

  posthogClient.capture(event.name, event.params);
}

function flushQueuedEvents() {
  if (!posthogClient || readAnalyticsConsent() !== ANALYTICS_CONSENT.ACCEPTED) return;

  const queued = eventQueue;
  eventQueue = [];
  queued.forEach(captureWithPostHog);
}

function getPostHogConfig() {
  return {
    api_host: getEnv("REACT_APP_POSTHOG_HOST") || DEFAULT_POSTHOG_HOST,
    defaults: "2026-01-30",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    person_profiles: "identified_only",
    request_batching: false,
    // Local browser QA often runs in HeadlessChrome, which PostHog drops as a bot by default.
    opt_out_useragent_filter: process.env.NODE_ENV === "development",
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "input, textarea, select, [data-ph-mask]",
      blockSelector: "[data-ph-block]",
    },
  };
}

async function hashLeadEmail(email) {
  if (!hasBrowser()) return "";
  if (!window.crypto?.subtle || typeof TextEncoder === "undefined") return "";

  const normalized = String(email || "").trim().toLowerCase();
  if (!normalized) return "";

  const digest = await window.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(normalized)
  );

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function sanitizeLeadProperties(properties = {}) {
  const blockedKeys = new Set([
    "email",
    "name",
    "first_name",
    "last_name",
    "phone",
    "company",
  ]);

  return Object.entries(properties).reduce((safeProperties, [key, value]) => {
    if (!blockedKeys.has(key) && value !== undefined && value !== null) {
      safeProperties[key] = value;
    }

    return safeProperties;
  }, {});
}

export async function initializeAnalytics() {
  if (!hasBrowser()) return null;
  if (readAnalyticsConsent() !== ANALYTICS_CONSENT.ACCEPTED) return null;

  if (!isExternalAnalyticsEnabled()) {
    eventQueue = [];
    return null;
  }

  if (posthogClient) {
    flushQueuedEvents();
    return posthogClient;
  }

  if (posthogInitPromise) return posthogInitPromise;

  posthogInitPromise = posthogLoader()
    .then((client) => {
      const token = getProjectToken();

      if (!token || typeof client?.init !== "function") {
        eventQueue = [];
        return null;
      }

      client.init(token, getPostHogConfig());
      posthogClient = client;

      if (process.env.NODE_ENV === "development") {
        window.__makdisPosthog = posthogClient;
      }

      flushQueuedEvents();
      return posthogClient;
    })
    .catch((error) => {
      posthogInitPromise = null;
      console.warn("Analytics failed to initialize", error);
      return null;
    });

  return posthogInitPromise;
}

function sendToExternalAnalytics(event) {
  const consent = readAnalyticsConsent();

  if (consent === ANALYTICS_CONSENT.REJECTED) return;

  if (consent === ANALYTICS_CONSENT.UNDECIDED) {
    pushQueue(event);
    return;
  }

  if (!isExternalAnalyticsEnabled()) return;

  if (posthogClient) {
    captureWithPostHog(event);
    return;
  }

  pushQueue(event);
  initializeAnalytics();
}

export function trackEvent(name, params = {}) {
  if (!hasBrowser()) return;

  const event = normalizeEvent(name, params);
  dispatchInternalEvent(event);

  sendToExternalAnalytics(event);
}

function getPageViewUrl(path) {
  if (!hasBrowser()) return path || "";
  if (window.location.pathname === path) return window.location.href;

  try {
    return new URL(path || "/", window.location.origin).toString();
  } catch {
    return window.location.href;
  }
}

export function trackPageView(path, metadata = {}) {
  const pageViewParams = {
    ...metadata,
    path,
  };
  const normalizedPageView = normalizeEvent("page_view", pageViewParams);

  trackEvent("page_view", pageViewParams);

  sendToExternalAnalytics({
    name: "$pageview",
    params: {
      ...normalizedPageView.params,
      $current_url: getPageViewUrl(path),
      $pathname: path,
      title: document.title,
    },
    options: { send_instantly: true },
  });
}

export function identifyLead(email, properties = {}) {
  if (!hasBrowser()) return;
  if (!email || readAnalyticsConsent() !== ANALYTICS_CONSENT.ACCEPTED) return;
  if (!isExternalAnalyticsEnabled()) return;

  initializeAnalytics().then((client) => {
    if (typeof client?.identify !== "function") return;

    leadIdHasher(email).then((hash) => {
      if (!hash) return;

      client.identify(`lead_${hash}`, sanitizeLeadProperties(properties));
    });
  });
}

export function acceptAnalyticsConsent() {
  persistAcceptedConsent();
  return initializeAnalytics();
}

export function rejectAnalyticsConsent() {
  persistRejectedConsent();
  eventQueue = [];

  if (typeof posthogClient?.opt_out_capturing === "function") {
    posthogClient.opt_out_capturing();
  }
}

export function resetAnalyticsConsent() {
  persistResetConsent();
  eventQueue = [];

  if (typeof posthogClient?.opt_out_capturing === "function") {
    posthogClient.opt_out_capturing();
  }
}

export { ANALYTICS_CONSENT, readAnalyticsConsent };

export function __setPostHogLoaderForTests(loader) {
  posthogLoader = loader;
}

export function __setLeadIdHasherForTests(hasher) {
  leadIdHasher = hasher;
}

export function __getQueuedEventCountForTests() {
  return eventQueue.length;
}

export function __resetAnalyticsForTests() {
  posthogClient = null;
  posthogInitPromise = null;
  eventQueue = [];
  posthogLoader = () => import("posthog-js").then((module) => module.default || module);
  leadIdHasher = hashLeadEmail;
}
